import React, { useEffect, useState } from 'react';
import { SAccordion } from '@chargebee/sting-react';
import { cs } from '@bbkAdminUtils/css-utils';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';

import './collapse-form-container.scss';

type Props = {
  title?: React.ReactNode;
  extraHeader?: React.ReactNode;
  subtitle?: string;
  classes?: string;
  isOpened?: boolean;
  renderChildrenIfClosed?: boolean;
  btnClasses?: string;
  onClick?: (newState: boolean) => unknown;
};

const CollapseFormContainer: ReactFCC<Props> = ({
  title = 'Show / Hide',
  subtitle,
  extraHeader,
  classes = 'CFC-default-theme',
  isOpened = true,
  renderChildrenIfClosed = true,
  btnClasses = 'CFC-button',
  onClick,
  children,
}) => {
  const [open, setOpen] = useState(isOpened);

  // Preserve the existing open-on-request contract; toggling remains local.
  useEffect(() => {
    if (isOpened) setOpen(true);
  }, [isOpened]);

  return (
    <SAccordion
      type="single"
      collapsible
      value={open ? 'content' : ''}
      onValueChange={(value) => {
        const nextOpen = value === 'content';
        onClick?.(nextOpen);
        setOpen(nextOpen);
      }}
      className={cs(
        'collapse-form-container tw-mb-3 !tw-overflow-visible tw-bg-white',
        classes,
        {
          'CFC-collapsed': !open,
          'CFC-expanded': open,
        }
      )}
    >
      <SAccordion.Item value="content">
        <div className="tw-flex tw-items-center tw-gap-2">
          <SAccordion.Trigger
            className={cs('tw-min-w-0 tw-flex-1 before:!tw-hidden', btnClasses)}
          >
            <span className="tw-flex tw-items-center tw-justify-between tw-gap-3">
              {title}
              {subtitle && (
                <span className="tw-text-sm tw-font-normal tw-text-neutral-600">
                  {subtitle}
                </span>
              )}
            </span>
          </SAccordion.Trigger>
          {extraHeader && (
            <div className="tw-shrink-0 tw-pr-4">{extraHeader}</div>
          )}
        </div>
        <SAccordion.Content
          forceMount={renderChildrenIfClosed ? true : undefined}
          hidden={!open}
          className="data-[state=closed]:!tw-hidden data-[state=open]:!tw-overflow-visible"
        >
          {(renderChildrenIfClosed || open) && children}
        </SAccordion.Content>
      </SAccordion.Item>
    </SAccordion>
  );
};

export default CollapseFormContainer;
